import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import axios from "axios";

const GET_SALES_API =
    "https://oppiesfunc.azurewebsites.net/api/getSalesByShopId?code=AJJ6BhJCb7kfUYKCWu8rt5Vy_4LnvfKV4uHNTgDaWrw8AzFuw07_8Q==";

const DELETE_SALE_API =
    "https://oppiesfunc.azurewebsites.net/api/deleteSale?code=Z8axfgfZV3kZ6b4OFJikEz_vqH2jUUSfsRYVhnLajBkaAzFuMEE86w==";

const CREATE_SALE_API =
    "https://oppiesfunc.azurewebsites.net/api/createSalesEvent?code=TJBXh5bi8oPzYgc-SMWsPcGz0zmI3LGZQLXB2udi2YOgAzFuL30M1g==";

const UPLOAD_SALE_IMAGE_API =
    "https://oppiesfunc.azurewebsites.net/api/uploadSaleImageWeb?code=yI6HgdP-65RdhnTfK6H15sPnwcL5ueNSUEDgbaJKA4rsAzFuyiVDrw==";

const MAX_IMAGE_SIZE =
    10 * 1024 * 1024;

const EMPTY_FORM = {
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    imageURL: "",
};

function SalesTab({
    shopId,
    userEmail,
}) {
    /*
     * =========================================================
     * AUTHENTICATED PORTAL ACCOUNT
     * =========================================================
     *
     * shopId
     *     = branch currently selected in Dashboard
     *
     * authenticatedEmail
     *     = actual account logged into the Portal
     *
     * For normal shops these belong to the same shop.
     * For managers, authenticatedEmail remains Head Office
     * while shopId changes as branches are selected.
     */

    const authenticatedEmail =
        (
            userEmail ||
            localStorage.getItem(
                "shopEmail"
            ) ||
            ""
        )
            .trim()
            .toLowerCase();

    const [sales, setSales] =
        useState([]);

    const [form, setForm] =
        useState(EMPTY_FORM);

    const [
        selectedImage,
        setSelectedImage,
    ] =
        useState(null);

    const [
        imagePreview,
        setImagePreview,
    ] =
        useState("");

    const [status, setStatus] =
        useState({
            type: "",
            message: "",
        });

    const [loading, setLoading] =
        useState(true);

    const [
        creating,
        setCreating,
    ] =
        useState(false);

    const [
        uploadingImage,
        setUploadingImage,
    ] =
        useState(false);

    const [
        deletingId,
        setDeletingId,
    ] =
        useState(null);

    const [
        draggingImage,
        setDraggingImage,
    ] =
        useState(false);

    const fileInputRef =
        useRef(null);

    /*
     * =========================================================
     * STATUS
     * =========================================================
     */

    const showStatus = (
        type,
        message
    ) => {
        setStatus({
            type,
            message,
        });
    };

    /*
     * =========================================================
     * SALES
     * =========================================================
     */

    const fetchSales =
        useCallback(
            async () => {
                if (!shopId) {
                    setSales([]);
                    setLoading(false);
                    return;
                }

                try {
                    setLoading(true);

                    const res =
                        await axios.get(
                            GET_SALES_API,
                            {
                                params: {
                                    shopId,
                                },
                            }
                        );

                    const result =
                        Array.isArray(
                            res.data
                        )
                            ? res.data
                            : Array.isArray(
                                res.data
                                    ?.sales
                            )
                                ? res.data
                                    .sales
                                : [];

                    setSales(result);
                } catch (err) {
                    console.error(
                        "❌ Failed to fetch sales:",
                        err?.response
                            ?.data ||
                        err?.message ||
                        err
                    );

                    setSales([]);

                    showStatus(
                        "error",
                        "We couldn't load your current sales."
                    );
                } finally {
                    setLoading(false);
                }
            },
            [shopId]
        );

    useEffect(() => {
        /*
         * Every time a manager switches branch,
         * shopId changes and we reload that
         * branch's sales.
         */

        setStatus({
            type: "",
            message: "",
        });

        fetchSales();
    }, [fetchSales]);

    /*
     * =========================================================
     * RESET CREATE FORM WHEN BRANCH CHANGES
     * =========================================================
     *
     * Prevent a manager from preparing a sale under Branch A,
     * switching to Branch B, and accidentally publishing the
     * old form against Branch B.
     */

    useEffect(() => {
        if (
            imagePreview &&
            imagePreview.startsWith(
                "blob:"
            )
        ) {
            URL.revokeObjectURL(
                imagePreview
            );
        }

        setForm(EMPTY_FORM);
        setSelectedImage(null);
        setImagePreview("");
        setDraggingImage(false);

        if (
            fileInputRef.current
        ) {
            fileInputRef.current.value =
                "";
        }
    }, [shopId]);

    /*
     * =========================================================
     * PREVIEW CLEANUP
     * =========================================================
     */

    useEffect(() => {
        return () => {
            if (
                imagePreview &&
                imagePreview.startsWith(
                    "blob:"
                )
            ) {
                URL.revokeObjectURL(
                    imagePreview
                );
            }
        };
    }, [imagePreview]);

    /*
     * =========================================================
     * FORM
     * =========================================================
     */

    const updateForm = (
        field,
        value
    ) => {
        setForm((current) => ({
            ...current,
            [field]: value,
        }));

        if (status.message) {
            setStatus({
                type: "",
                message: "",
            });
        }
    };

    /*
     * =========================================================
     * NZ DATE
     * =========================================================
     */

    const parseNZDate = (
        dateStr
    ) => {
        if (!dateStr) {
            return null;
        }

        const trimmed =
            dateStr.trim();

        const match =
            trimmed.match(
                /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/
            );

        if (!match) {
            return null;
        }

        const day =
            Number(match[1]);

        const month =
            Number(match[2]);

        const year =
            Number(match[3]);

        if (
            day < 1 ||
            day > 31 ||
            month < 1 ||
            month > 12 ||
            year < 2000
        ) {
            return null;
        }

        const testDate =
            new Date(
                Date.UTC(
                    year,
                    month - 1,
                    day
                )
            );

        if (
            testDate.getUTCFullYear() !==
            year ||
            testDate.getUTCMonth() !==
            month - 1 ||
            testDate.getUTCDate() !==
            day
        ) {
            return null;
        }

        return `${year}-${String(
            month
        ).padStart(
            2,
            "0"
        )}-${String(
            day
        ).padStart(
            2,
            "0"
        )}`;
    };

    const formatDisplayDate = (
        value
    ) => {
        if (!value) {
            return "";
        }

        const isoMatch =
            String(value).match(
                /^(\d{4})-(\d{2})-(\d{2})$/
            );

        if (isoMatch) {
            const date =
                new Date(
                    Number(
                        isoMatch[1]
                    ),
                    Number(
                        isoMatch[2]
                    ) - 1,
                    Number(
                        isoMatch[3]
                    )
                );

            return date
                .toLocaleDateString(
                    "en-NZ",
                    {
                        day:
                            "numeric",
                        month:
                            "short",
                        year:
                            "numeric",
                    }
                );
        }

        const date =
            new Date(value);

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return value;
        }

        return date
            .toLocaleDateString(
                "en-NZ",
                {
                    day:
                        "numeric",
                    month:
                        "short",
                    year:
                        "numeric",
                }
            );
    };

    /*
     * =========================================================
     * LOCAL IMAGE SELECTION
     * =========================================================
     */

    const applySelectedImage = (
        file
    ) => {
        if (!file) return;

        if (
            !file.type ||
            !file.type.startsWith(
                "image/"
            )
        ) {
            showStatus(
                "error",
                "Please select a valid image file."
            );

            return;
        }

        if (
            file.size >
            MAX_IMAGE_SIZE
        ) {
            showStatus(
                "error",
                "The image must be smaller than 10 MB."
            );

            return;
        }

        if (
            imagePreview &&
            imagePreview.startsWith(
                "blob:"
            )
        ) {
            URL.revokeObjectURL(
                imagePreview
            );
        }

        const preview =
            URL.createObjectURL(
                file
            );

        setSelectedImage(file);
        setImagePreview(
            preview
        );

        /*
         * Local upload takes priority
         * over external URL.
         */

        setForm(
            (current) => ({
                ...current,
                imageURL: "",
            })
        );

        setStatus({
            type: "",
            message: "",
        });
    };

    const handleImageSelect = (
        event
    ) => {
        const file =
            event.target
                .files?.[0];

        if (!file) return;

        applySelectedImage(
            file
        );
    };

    const removeSelectedImage =
        () => {
            if (
                imagePreview &&
                imagePreview.startsWith(
                    "blob:"
                )
            ) {
                URL.revokeObjectURL(
                    imagePreview
                );
            }

            setSelectedImage(
                null
            );

            setImagePreview("");

            if (
                fileInputRef.current
            ) {
                fileInputRef
                    .current
                    .value = "";
            }
        };

    /*
     * =========================================================
     * DRAG / DROP
     * =========================================================
     */

    const handleDragOver = (
        event
    ) => {
        event.preventDefault();
        event.stopPropagation();

        setDraggingImage(
            true
        );
    };

    const handleDragLeave = (
        event
    ) => {
        event.preventDefault();
        event.stopPropagation();

        setDraggingImage(
            false
        );
    };

    const handleDrop = (
        event
    ) => {
        event.preventDefault();
        event.stopPropagation();

        setDraggingImage(
            false
        );

        const file =
            event.dataTransfer
                .files?.[0];

        if (!file) return;

        applySelectedImage(
            file
        );
    };

    /*
     * =========================================================
     * IMAGE UPLOAD
     * =========================================================
     */

    const uploadSaleImage =
        async () => {
            /*
             * If there is no local image,
             * preserve the optional external
             * image URL behaviour.
             */

            if (!selectedImage) {
                return form
                    .imageURL
                    .trim();
            }

            if (!shopId) {
                throw new Error(
                    "Shop ID is unavailable."
                );
            }

            if (
                !authenticatedEmail
            ) {
                throw new Error(
                    "Account email is unavailable."
                );
            }

            setUploadingImage(
                true
            );

            try {
                const uploadData =
                    new FormData();

                /*
                 * Selected branch.
                 */

                uploadData.append(
                    "shopId",
                    shopId
                );

                /*
                 * Actual Portal account.
                 *
                 * uploadSaleImageWeb will use
                 * this with canManageShop().
                 */

                uploadData.append(
                    "userEmail",
                    authenticatedEmail
                );

                /*
                 * uploadSaleImageWeb expects
                 * field name: image
                 */

                uploadData.append(
                    "image",
                    selectedImage
                );

                const res =
                    await axios.post(
                        UPLOAD_SALE_IMAGE_API,
                        uploadData
                    );

                const uploadedURL =
                    res.data
                        ?.imageURL ||
                    res.data
                        ?.imageUrl ||
                    "";

                if (
                    !uploadedURL
                ) {
                    throw new Error(
                        "The upload completed but no image URL was returned."
                    );
                }

                return uploadedURL;
            } finally {
                setUploadingImage(
                    false
                );
            }
        };

    /*
     * =========================================================
     * DELETE SALE
     * =========================================================
     */

    const handleDelete =
        async (id) => {
            if (
                !id ||
                deletingId
            ) {
                return;
            }

            if (!shopId) {
                showStatus(
                    "error",
                    "Your shop ID is unavailable. Please sign in again."
                );

                return;
            }

            if (
                !authenticatedEmail
            ) {
                showStatus(
                    "error",
                    "Your account email is unavailable. Please sign in again."
                );

                return;
            }

            const confirmed =
                window.confirm(
                    "Delete this sale? This cannot be undone."
                );

            if (!confirmed) {
                return;
            }

            try {
                setDeletingId(
                    id
                );

                setStatus({
                    type: "",
                    message: "",
                });

                /*
                 * Existing deleteSale Function
                 * expects lowercase shopid.
                 */

                await axios.post(
                    DELETE_SALE_API,
                    {
                        id,

                        shopid:
                            shopId,

                        /*
                         * Actual logged-in
                         * Portal account.
                         */

                        userEmail:
                            authenticatedEmail,
                    }
                );

                await fetchSales();

                showStatus(
                    "success",
                    "Sale deleted successfully."
                );
            } catch (err) {
                console.error(
                    "❌ Delete sale error:",
                    err?.response
                        ?.data ||
                    err?.message ||
                    err
                );

                if (
                    err?.response
                        ?.status ===
                    403
                ) {
                    showStatus(
                        "error",
                        err?.response
                            ?.data
                            ?.error ||
                        "You are not authorised to manage this shop."
                    );

                    return;
                }

                showStatus(
                    "error",
                    err?.response
                        ?.data
                        ?.error ||
                    "We couldn't delete this sale. Please try again."
                );
            } finally {
                setDeletingId(
                    null
                );
            }
        };

    /*
     * =========================================================
     * CREATE SALE
     * =========================================================
     */

    const handleCreate =
        async () => {
            if (creating) {
                return;
            }

            const title =
                form.title
                    .trim();

            const description =
                form.description
                    .trim();

            const formattedStart =
                parseNZDate(
                    form.startDate
                );

            const formattedEnd =
                parseNZDate(
                    form.endDate
                );

            if (
                !title ||
                !form.startDate
                    .trim() ||
                !form.endDate
                    .trim()
            ) {
                showStatus(
                    "error",
                    "Please complete the title, start date and end date."
                );

                return;
            }

            if (
                !formattedStart ||
                !formattedEnd
            ) {
                showStatus(
                    "error",
                    "Please enter valid dates in dd/mm/yyyy format."
                );

                return;
            }

            if (
                formattedEnd <
                formattedStart
            ) {
                showStatus(
                    "error",
                    "The end date cannot be before the start date."
                );

                return;
            }

            if (!shopId) {
                showStatus(
                    "error",
                    "Your shop ID is unavailable. Please sign in again."
                );

                return;
            }

            if (
                !authenticatedEmail
            ) {
                showStatus(
                    "error",
                    "Your account email is unavailable. Please sign in again."
                );

                return;
            }

            try {
                setCreating(
                    true
                );

                /*
                 * STEP 1:
                 * Upload local image
                 * if selected.
                 */

                if (
                    selectedImage
                ) {
                    showStatus(
                        "info",
                        "Uploading your sale image..."
                    );
                } else {
                    showStatus(
                        "info",
                        "Creating your sale..."
                    );
                }

                let finalImageURL =
                    "";

                try {
                    finalImageURL =
                        await uploadSaleImage();
                } catch (
                uploadErr
                ) {
                    console.error(
                        "❌ Sale image upload failed:",
                        uploadErr
                            ?.response
                            ?.data ||
                        uploadErr
                            ?.message ||
                        uploadErr
                    );

                    if (
                        uploadErr
                            ?.response
                            ?.status ===
                        403
                    ) {
                        showStatus(
                            "error",
                            uploadErr
                                ?.response
                                ?.data
                                ?.error ||
                            "You are not authorised to manage this shop."
                        );

                        return;
                    }

                    showStatus(
                        "error",
                        uploadErr
                            ?.response
                            ?.data
                            ?.error ||
                        "We couldn't upload the sale image. The sale has not been created."
                    );

                    return;
                }

                /*
                 * STEP 2:
                 * Create sale.
                 *
                 * createSalesEvent itself:
                 *
                 * - authorises shop access
                 * - writes sale to Cosmos
                 * - retrieves shop.location
                 * - sends sale push notification
                 * - adds in-app notifications
                 *
                 * SalesTab deliberately does NOT
                 * call notification Functions
                 * separately.
                 */

                showStatus(
                    "info",
                    "Creating your sale..."
                );

                await axios.post(
                    CREATE_SALE_API,
                    {
                        /*
                         * Selected branch.
                         */

                        shopId,

                        /*
                         * Actual logged-in account.
                         */

                        userEmail:
                            authenticatedEmail,

                        title,

                        description,

                        startDate:
                            formattedStart,

                        endDate:
                            formattedEnd,

                        imageURL:
                            finalImageURL ||
                            "",
                    }
                );

                /*
                 * Refresh from Cosmos rather
                 * than relying on response
                 * shape.
                 */

                await fetchSales();

                /*
                 * Reset form.
                 */

                if (
                    imagePreview &&
                    imagePreview
                        .startsWith(
                            "blob:"
                        )
                ) {
                    URL.revokeObjectURL(
                        imagePreview
                    );
                }

                setSelectedImage(
                    null
                );

                setImagePreview(
                    ""
                );

                setForm(
                    EMPTY_FORM
                );

                if (
                    fileInputRef.current
                ) {
                    fileInputRef
                        .current
                        .value =
                        "";
                }

                /*
                 * createSalesEvent catches
                 * notification failures
                 * internally, so frontend
                 * says only that the sale
                 * itself was created.
                 */

                showStatus(
                    "success",
                    "Sale created successfully."
                );
            } catch (err) {
                console.error(
                    "❌ Create sale error:",
                    err?.response
                        ?.data ||
                    err?.message ||
                    err
                );

                if (
                    err?.response
                        ?.status ===
                    403
                ) {
                    showStatus(
                        "error",
                        err?.response
                            ?.data
                            ?.error ||
                        "You are not authorised to manage this shop."
                    );

                    return;
                }

                showStatus(
                    "error",
                    err?.response
                        ?.data
                        ?.error ||
                    "We couldn't create this sale. Please try again."
                );
            } finally {
                setCreating(
                    false
                );

                setUploadingImage(
                    false
                );
            }
        };

    /*
     * =========================================================
     * IMAGE PREVIEW
     * =========================================================
     */

    const externalPreview =
        !selectedImage &&
            form.imageURL.trim()
            ? form.imageURL
                .trim()
            : "";

    const currentPreview =
        imagePreview ||
        externalPreview;

    /*
     * =========================================================
     * RENDER
     * =========================================================
     */

    return (
        <>
            <style>
                {salesStyles}
            </style>

            <div className="sales-page">

                {/* ==================================================
                    INTRO
                ================================================== */}

                <section className="sales-intro">

                    <div className="sales-eyebrow">
                        Sales & Promotions
                    </div>

                    <h1>
                        Promote your latest sale
                    </h1>

                    <p>
                        Create sale events for your
                        Op Shop and keep OPPIES
                        shoppers up to date with
                        what&apos;s happening in
                        store.
                    </p>

                </section>

                {/* ==================================================
                    STATUS
                ================================================== */}

                {status.message ? (
                    <div
                        className={
                            `sales-status ${status.type
                                ? `sales-status-${status.type}`
                                : ""
                            }`
                        }
                    >

                        <div className="sales-status-icon">
                            {status.type ===
                                "success"
                                ? "✓"
                                : status.type ===
                                    "error"
                                    ? "!"
                                    : status.type ===
                                        "warning"
                                        ? "!"
                                        : "•"}
                        </div>

                        <span>
                            {status.message}
                        </span>

                    </div>
                ) : null}

                {/* ==================================================
                    CURRENT SALES
                ================================================== */}

                <section className="sales-current-panel">

                    <div className="sales-panel-heading">

                        <div>

                            <div className="sales-panel-eyebrow">
                                Current Promotions
                            </div>

                            <h2>
                                Your Sales
                            </h2>

                            <p>
                                Sales currently
                                published for this
                                Op Shop.
                            </p>

                        </div>

                        <div className="sales-count-badge">
                            {loading
                                ? "..."
                                : sales.length}
                        </div>

                    </div>

                    {loading ? (

                        <div className="sales-loading-state">

                            <span className="sales-large-spinner" />

                            <div>
                                Loading sales...
                            </div>

                        </div>

                    ) : sales.length ===
                        0 ? (

                        <div className="sales-empty-state">

                            <div className="sales-empty-icon">

                                <svg
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M20.59 13.41 11 3.83V3H4v7h.83l9.58 9.59a2 2 0 0 0 2.82 0l3.36-3.36a2 2 0 0 0 0-2.82Z" />

                                    <circle
                                        cx="7.5"
                                        cy="6.5"
                                        r=".5"
                                        fill="currentColor"
                                    />
                                </svg>

                            </div>

                            <h3>
                                No active sales yet
                            </h3>

                            <p>
                                Create your first sale
                                below and it will appear
                                here once published.
                            </p>

                        </div>

                    ) : (

                        <div className="sales-card-grid">

                            {sales.map(
                                (sale) => (

                                    <article
                                        className="sales-card"
                                        key={
                                            sale.id
                                        }
                                    >

                                        {sale.imageURL ||
                                            sale.imageUrl ? (

                                            <div className="sales-card-image-wrap">

                                                <img
                                                    src={
                                                        sale.imageURL ||
                                                        sale.imageUrl
                                                    }
                                                    alt={
                                                        sale.title ||
                                                        "Sale"
                                                    }
                                                    className="sales-card-image"
                                                    onError={(
                                                        event
                                                    ) => {
                                                        event.currentTarget.style.display =
                                                            "none";
                                                    }}
                                                />

                                            </div>

                                        ) : null}

                                        <div className="sales-card-content">

                                            <div className="sales-card-top">

                                                <span className="sales-live-pill">
                                                    Sale Event
                                                </span>

                                            </div>

                                            <h3>
                                                {sale.title ||
                                                    "Shop Sale"}
                                            </h3>

                                            <div className="sales-date-row">

                                                <svg
                                                    viewBox="0 0 24 24"
                                                    width="16"
                                                    height="16"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <rect
                                                        x="3"
                                                        y="5"
                                                        width="18"
                                                        height="16"
                                                        rx="2"
                                                    />

                                                    <path d="M16 3v4M8 3v4M3 11h18" />
                                                </svg>

                                                <span>
                                                    {formatDisplayDate(
                                                        sale.startDate
                                                    )}

                                                    {" → "}

                                                    {formatDisplayDate(
                                                        sale.endDate
                                                    )}
                                                </span>

                                            </div>

                                            {sale.description ? (

                                                <p className="sales-description">
                                                    {sale.description}
                                                </p>

                                            ) : null}

                                            <button
                                                type="button"
                                                className="sales-delete-button"
                                                disabled={
                                                    deletingId ===
                                                    sale.id
                                                }
                                                onClick={() =>
                                                    handleDelete(
                                                        sale.id
                                                    )
                                                }
                                            >

                                                {deletingId ===
                                                    sale.id ? (
                                                    <>
                                                        <span className="sales-button-spinner" />

                                                        Deleting...
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg
                                                            viewBox="0 0 24 24"
                                                            width="15"
                                                            height="15"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="1.9"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <path d="M3 6h18" />

                                                            <path d="M8 6V4h8v2" />

                                                            <path d="M19 6 18 21H6L5 6" />

                                                            <path d="M10 11v5M14 11v5" />
                                                        </svg>

                                                        Delete Sale
                                                    </>
                                                )}

                                            </button>

                                        </div>

                                    </article>

                                )
                            )}

                        </div>

                    )}

                </section>

                {/* ==================================================
                    CREATE SALE
                ================================================== */}

                <section className="sales-create-panel">

                    <div className="sales-create-heading">

                        <div>

                            <div className="sales-panel-eyebrow">
                                New Promotion
                            </div>

                            <h2>
                                Create New Sale
                            </h2>

                            <p>
                                Add the details shoppers
                                should see when you
                                publish this sale.
                            </p>

                        </div>

                        <div className="sales-create-icon">

                            <svg
                                viewBox="0 0 24 24"
                                width="22"
                                height="22"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 5v14M5 12h14" />
                            </svg>

                        </div>

                    </div>

                    <div className="sales-form">

                        {/* TITLE */}

                        <div className="sales-field sales-field-full">

                            <label htmlFor="sale-title">
                                Sale Title
                                <span>*</span>
                            </label>

                            <input
                                id="sale-title"
                                type="text"
                                maxLength={
                                    100
                                }
                                placeholder="e.g. Half Price Clothing Sale"
                                value={
                                    form.title
                                }
                                disabled={
                                    creating
                                }
                                onChange={(
                                    event
                                ) =>
                                    updateForm(
                                        "title",
                                        event.target
                                            .value
                                    )
                                }
                            />

                            <div className="sales-field-help">
                                A short title
                                shoppers will
                                recognise quickly.
                            </div>

                        </div>

                        {/* START DATE */}

                        <div className="sales-field">

                            <label htmlFor="sale-start-date">
                                Start Date
                                <span>*</span>
                            </label>

                            <input
                                id="sale-start-date"
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                placeholder="dd/mm/yyyy"
                                value={
                                    form.startDate
                                }
                                disabled={
                                    creating
                                }
                                onChange={(
                                    event
                                ) =>
                                    updateForm(
                                        "startDate",
                                        event.target
                                            .value
                                    )
                                }
                            />

                            <div className="sales-field-help">
                                Enter the date as
                                dd/mm/yyyy.
                            </div>

                        </div>

                        {/* END DATE */}

                        <div className="sales-field">

                            <label htmlFor="sale-end-date">
                                End Date
                                <span>*</span>
                            </label>

                            <input
                                id="sale-end-date"
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                placeholder="dd/mm/yyyy"
                                value={
                                    form.endDate
                                }
                                disabled={
                                    creating
                                }
                                onChange={(
                                    event
                                ) =>
                                    updateForm(
                                        "endDate",
                                        event.target
                                            .value
                                    )
                                }
                            />

                            <div className="sales-field-help">
                                The sale remains
                                visible through this
                                date.
                            </div>

                        </div>

                        {/* DESCRIPTION */}

                        <div className="sales-field sales-field-full">

                            <label htmlFor="sale-description">
                                Description
                            </label>

                            <textarea
                                id="sale-description"
                                rows="5"
                                maxLength={
                                    600
                                }
                                placeholder="Tell shoppers what's included in the sale..."
                                value={
                                    form.description
                                }
                                disabled={
                                    creating
                                }
                                onChange={(
                                    event
                                ) =>
                                    updateForm(
                                        "description",
                                        event.target
                                            .value
                                    )
                                }
                            />

                            <div className="sales-field-meta">

                                <span>
                                    Optional
                                </span>

                                <span>
                                    {
                                        form
                                            .description
                                            .length
                                    }
                                    /600
                                </span>

                            </div>

                        </div>

                        {/* IMAGE */}

                        <div className="sales-field sales-field-full">

                            <label>
                                Sale Image
                            </label>

                            <div
                                className={
                                    `sales-upload ${draggingImage
                                        ? "sales-upload-dragging"
                                        : ""
                                    }`
                                }
                                onDragOver={
                                    handleDragOver
                                }
                                onDragLeave={
                                    handleDragLeave
                                }
                                onDrop={
                                    handleDrop
                                }
                            >

                                <input
                                    ref={
                                        fileInputRef
                                    }
                                    type="file"
                                    accept="image/*"
                                    className="sales-file-input"
                                    disabled={
                                        creating
                                    }
                                    onChange={
                                        handleImageSelect
                                    }
                                />

                                {currentPreview ? (

                                    <div className="sales-image-preview">

                                        <img
                                            src={
                                                currentPreview
                                            }
                                            alt="Sale preview"
                                        />

                                        {selectedImage ? (

                                            <button
                                                type="button"
                                                className="sales-remove-image"
                                                disabled={
                                                    creating
                                                }
                                                onClick={
                                                    removeSelectedImage
                                                }
                                            >
                                                Remove
                                            </button>

                                        ) : null}

                                    </div>

                                ) : (

                                    <div className="sales-upload-content">

                                        <div className="sales-upload-icon">

                                            <svg
                                                viewBox="0 0 24 24"
                                                width="24"
                                                height="24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.8"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <rect
                                                    x="3"
                                                    y="3"
                                                    width="18"
                                                    height="18"
                                                    rx="3"
                                                />

                                                <circle
                                                    cx="8.5"
                                                    cy="8.5"
                                                    r="1.5"
                                                />

                                                <path d="m21 15-5-5L5 21" />
                                            </svg>

                                        </div>

                                        <h3>
                                            Upload a sale
                                            image
                                        </h3>

                                        <p>
                                            Drag and drop
                                            an image here,
                                            or choose one
                                            from your
                                            computer.
                                        </p>

                                        <button
                                            type="button"
                                            className="sales-upload-button"
                                            disabled={
                                                creating
                                            }
                                            onClick={() =>
                                                fileInputRef
                                                    .current
                                                    ?.click()
                                            }
                                        >
                                            Choose Image
                                        </button>

                                        <span>
                                            JPG, PNG or
                                            other common
                                            image formats.
                                            Maximum 10 MB.
                                        </span>

                                    </div>

                                )}

                            </div>

                        </div>

                        {/* OPTIONAL EXTERNAL IMAGE URL */}

                        <div className="sales-field sales-field-full">

                            <label htmlFor="sale-image-url">
                                Existing Image URL
                            </label>

                            <input
                                id="sale-image-url"
                                type="url"
                                placeholder="https://..."
                                value={
                                    form.imageURL
                                }
                                disabled={
                                    creating ||
                                    Boolean(
                                        selectedImage
                                    )
                                }
                                onChange={(
                                    event
                                ) =>
                                    updateForm(
                                        "imageURL",
                                        event.target
                                            .value
                                    )
                                }
                            />

                            <div className="sales-field-help">
                                Optional. You can
                                paste an existing
                                image URL instead of
                                uploading a file.
                            </div>

                        </div>

                        {/* ACTION */}

                        <div className="sales-form-actions">

                            <div className="sales-publish-note">

                                <svg
                                    viewBox="0 0 24 24"
                                    width="17"
                                    height="17"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z" />

                                    <path d="M12 16v-4M12 8h.01" />
                                </svg>

                                <span>
                                    Publishing a
                                    sale may notify
                                    nearby shoppers
                                    and people who
                                    follow this Op
                                    Shop.
                                </span>

                            </div>

                            <button
                                type="button"
                                className="sales-create-button"
                                disabled={
                                    creating ||
                                    uploadingImage
                                }
                                onClick={
                                    handleCreate
                                }
                            >

                                {creating ? (
                                    <>
                                        <span className="sales-button-spinner sales-button-spinner-light" />

                                        {uploadingImage
                                            ? "Uploading..."
                                            : "Creating Sale..."}
                                    </>
                                ) : (
                                    <>
                                        <svg
                                            viewBox="0 0 24 24"
                                            width="17"
                                            height="17"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.9"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M12 5v14M5 12h14" />
                                        </svg>

                                        Create Sale
                                    </>
                                )}

                            </button>

                        </div>

                    </div>

                </section>

            </div>
        </>
    );
}

export default SalesTab;


/*
 * =============================================================
 * STYLES
 * =============================================================
 */

const salesStyles = `
    .sales-page {
        width: 100%;
        max-width: 1180px;
        margin: 0 auto;
        padding: 4px 2px 50px;
        box-sizing: border-box;
        color: #1f2933;
    }

    .sales-page * {
        box-sizing: border-box;
    }

    .sales-intro {
        margin-bottom: 26px;
    }

    .sales-eyebrow,
    .sales-panel-eyebrow {
        font-size: 11px;
        line-height: 1.4;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 2.1px;
        color: #17745b;
        margin-bottom: 8px;
    }

    .sales-intro h1 {
        margin: 0;
        max-width: 720px;
        font-size: clamp(
            28px,
            4vw,
            40px
        );
        line-height: 1.08;
        font-weight: 720;
        letter-spacing: -1.7px;
        color: #19231f;
    }

    .sales-intro p {
        margin: 13px 0 0;
        max-width: 690px;
        color: #6d7973;
        font-size: 15px;
        line-height: 1.75;
    }

    /*
     * STATUS
     */

    .sales-status {
        display: flex;
        align-items: flex-start;
        gap: 11px;
        width: 100%;
        margin-bottom: 22px;
        padding: 13px 15px;
        border-radius: 12px;
        border: 1px solid #dfe6e2;
        background: #f8faf9;
        color: #52605a;
        font-size: 13px;
        line-height: 1.55;
    }

    .sales-status-icon {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 21px;
        height: 21px;
        border-radius: 50%;
        font-size: 12px;
        font-weight: 800;
        background: #e7ece9;
        color: #52605a;
    }

    .sales-status-success {
        border-color: #c8e3d7;
        background: #f1faf6;
        color: #23664f;
    }

    .sales-status-success
    .sales-status-icon {
        background: #dcefe6;
        color: #176447;
    }

    .sales-status-error {
        border-color: #efd1cd;
        background: #fff7f6;
        color: #8b433c;
    }

    .sales-status-error
    .sales-status-icon {
        background: #f8dfdc;
        color: #983f38;
    }

    .sales-status-warning {
        border-color: #eadcb5;
        background: #fffaf0;
        color: #806824;
    }

    .sales-status-warning
    .sales-status-icon {
        background: #f5e9c6;
        color: #78601a;
    }

    .sales-status-info {
        border-color: #cfe2db;
        background: #f4faf7;
        color: #316454;
    }

    /*
     * SHARED PANELS
     */

    .sales-current-panel,
    .sales-create-panel {
        background: #ffffff;
        border: 1px solid #e5e9e7;
        border-radius: 18px;
        box-shadow:
            0 1px 2px rgba(
                30,
                51,
                42,
                0.02
            ),
            0 12px 36px rgba(
                30,
                51,
                42,
                0.035
            );
        overflow: hidden;
    }

    .sales-current-panel {
        margin-bottom: 25px;
    }

    .sales-panel-heading,
    .sales-create-heading {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 22px;
        padding: 24px 25px 21px;
        border-bottom: 1px solid #edf0ee;
    }

    .sales-panel-heading h2,
    .sales-create-heading h2 {
        margin: 0;
        color: #202a26;
        font-size: 21px;
        line-height: 1.25;
        letter-spacing: -0.55px;
        font-weight: 720;
    }

    .sales-panel-heading p,
    .sales-create-heading p {
        margin: 7px 0 0;
        color: #748079;
        font-size: 13px;
        line-height: 1.6;
    }

    .sales-count-badge {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 40px;
        height: 40px;
        padding: 0 12px;
        border-radius: 12px;
        background: #f2f7f4;
        border: 1px solid #dfebe4;
        color: #257057;
        font-size: 14px;
        font-weight: 800;
    }

    /*
     * LOADING / EMPTY
     */

    .sales-loading-state {
        min-height: 190px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 13px;
        padding: 30px;
        color: #76817c;
        font-size: 13px;
    }

    .sales-large-spinner {
        display: inline-block;
        width: 28px;
        height: 28px;
        border: 3px solid #e0e8e4;
        border-top-color: #23775d;
        border-radius: 50%;
        animation:
            salesSpin
            0.8s
            linear
            infinite;
    }

    .sales-empty-state {
        min-height: 210px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 34px 24px;
    }

    .sales-empty-icon {
        width: 52px;
        height: 52px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 14px;
        border-radius: 15px;
        background: #f1f6f3;
        color: #257057;
    }

    .sales-empty-state h3 {
        margin: 0;
        color: #29332f;
        font-size: 17px;
        font-weight: 700;
        letter-spacing: -0.3px;
    }

    .sales-empty-state p {
        margin: 8px 0 0;
        max-width: 430px;
        color: #7a847f;
        font-size: 13px;
        line-height: 1.65;
    }

    /*
     * SALE CARDS
     */

    .sales-card-grid {
        display: grid;
        grid-template-columns:
            repeat(
                2,
                minmax(
                    0,
                    1fr
                )
            );
        gap: 17px;
        padding: 20px;
    }

    .sales-card {
        min-width: 0;
        overflow: hidden;
        border: 1px solid #e4e9e6;
        border-radius: 15px;
        background: #ffffff;
        box-shadow:
            0 7px 20px rgba(
                22,
                46,
                36,
                0.035
            );
    }

    .sales-card-image-wrap {
        width: 100%;
        height: 190px;
        overflow: hidden;
        background: #eef3f0;
        border-bottom: 1px solid #edf0ee;
    }

    .sales-card-image {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .sales-card-content {
        padding: 18px;
    }

    .sales-card-top {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
    }

    .sales-live-pill {
        display: inline-flex;
        align-items: center;
        min-height: 25px;
        padding: 4px 9px;
        border-radius: 999px;
        background: #edf7f2;
        border: 1px solid #d8ebe1;
        color: #236b52;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.7px;
        text-transform: uppercase;
    }

    .sales-card h3 {
        margin: 0;
        color: #27312d;
        font-size: 17px;
        line-height: 1.35;
        font-weight: 730;
        letter-spacing: -0.35px;
        overflow-wrap: anywhere;
    }

    .sales-date-row {
        display: flex;
        align-items: center;
        gap: 7px;
        margin-top: 10px;
        color: #68746e;
        font-size: 12px;
        line-height: 1.4;
    }

    .sales-date-row svg {
        color: #338069;
        flex: 0 0 auto;
    }

    .sales-description {
        margin: 13px 0 0;
        color: #68736e;
        font-size: 13px;
        line-height: 1.65;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
    }

    .sales-delete-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        min-height: 36px;
        margin-top: 17px;
        padding: 8px 13px;
        border: 1px solid #eccfc9;
        border-radius: 10px;
        background: #fff9f8;
        color: #934b42;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        transition:
            background 0.15s ease,
            border-color 0.15s ease,
            transform 0.15s ease;
    }

    .sales-delete-button:hover:not(
        :disabled
    ) {
        background: #fff2f0;
        border-color: #e4bdb6;
        transform: translateY(
            -1px
        );
    }

    .sales-delete-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    /*
     * CREATE PANEL
     */

    .sales-create-icon {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 42px;
        border-radius: 13px;
        background: #eff7f3;
        border: 1px solid #dcebe3;
        color: #27765c;
    }

    .sales-form {
        display: grid;
        grid-template-columns:
            repeat(
                2,
                minmax(
                    0,
                    1fr
                )
            );
        gap: 20px;
        padding: 24px 25px 27px;
    }

    .sales-field {
        min-width: 0;
    }

    .sales-field-full {
        grid-column: 1 / -1;
    }

    .sales-field label {
        display: block;
        margin-bottom: 7px;
        color: #35413c;
        font-size: 12px;
        line-height: 1.4;
        font-weight: 730;
    }

    .sales-field label span {
        margin-left: 3px;
        color: #a84c43;
    }

    .sales-field input,
    .sales-field textarea {
        display: block;
        width: 100%;
        border: 1px solid #dce3df;
        border-radius: 11px;
        outline: none;
        background: #ffffff;
        color: #27322d;
        font-family: inherit;
        font-size: 13px;
        line-height: 1.5;
        transition:
            border-color 0.15s ease,
            box-shadow 0.15s ease,
            background 0.15s ease;
    }

    .sales-field input {
        height: 43px;
        padding: 0 13px;
    }

    .sales-field textarea {
        min-height: 115px;
        padding: 11px 13px;
        resize: vertical;
    }

    .sales-field input::placeholder,
    .sales-field textarea::placeholder {
        color: #9da6a1;
    }

    .sales-field input:focus,
    .sales-field textarea:focus {
        border-color: #7eae9e;
        box-shadow:
            0 0 0 3px
            rgba(
                41,
                118,
                92,
                0.08
            );
    }

    .sales-field input:disabled,
    .sales-field textarea:disabled {
        cursor: not-allowed;
        background: #f4f6f5;
        color: #8b9590;
    }

    .sales-field-help {
        margin-top: 6px;
        color: #89928e;
        font-size: 11px;
        line-height: 1.5;
    }

    .sales-field-meta {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        margin-top: 6px;
        color: #89928e;
        font-size: 11px;
        line-height: 1.4;
    }

    /*
     * IMAGE UPLOAD
     */

    .sales-upload {
        position: relative;
        width: 100%;
        min-height: 230px;
        border: 1.5px dashed #ccd9d2;
        border-radius: 14px;
        background: #fafcfb;
        overflow: hidden;
        transition:
            border-color 0.15s ease,
            background 0.15s ease,
            box-shadow 0.15s ease;
    }

    .sales-upload:hover,
    .sales-upload-dragging {
        border-color: #63a088;
        background: #f6faf8;
        box-shadow:
            0 0 0 3px
            rgba(
                37,
                112,
                87,
                0.05
            );
    }

    .sales-file-input {
        display: none;
    }

    .sales-upload-content {
        min-height: 230px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 28px 20px;
    }

    .sales-upload-icon {
        width: 49px;
        height: 49px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 13px;
        border-radius: 14px;
        background: #edf6f1;
        color: #28755b;
    }

    .sales-upload-content h3 {
        margin: 0;
        color: #344039;
        font-size: 15px;
        line-height: 1.35;
        font-weight: 720;
    }

    .sales-upload-content p {
        margin: 7px 0 0;
        max-width: 390px;
        color: #818b86;
        font-size: 12px;
        line-height: 1.6;
    }

    .sales-upload-content span {
        margin-top: 11px;
        color: #9aa29e;
        font-size: 10px;
        line-height: 1.45;
    }

    .sales-upload-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 36px;
        margin-top: 14px;
        padding: 8px 15px;
        border: 1px solid #d7e2dc;
        border-radius: 10px;
        background: #ffffff;
        color: #2c7058;
        font-size: 12px;
        font-weight: 730;
        cursor: pointer;
        transition:
            background 0.15s ease,
            border-color 0.15s ease,
            transform 0.15s ease;
    }

    .sales-upload-button:hover:not(
        :disabled
    ) {
        background: #f5faf7;
        border-color: #bed5c8;
        transform: translateY(
            -1px
        );
    }

    .sales-upload-button:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }

    .sales-image-preview {
        position: relative;
        width: 100%;
        height: 310px;
        background: #edf1ef;
    }

    .sales-image-preview img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .sales-remove-image {
        position: absolute;
        top: 12px;
        right: 12px;
        min-height: 34px;
        padding: 7px 12px;
        border: 1px solid rgba(
            255,
            255,
            255,
            0.55
        );
        border-radius: 9px;
        background: rgba(
            31,
            41,
            37,
            0.82
        );
        color: #ffffff;
        backdrop-filter:
            blur(6px);
        font-size: 11px;
        font-weight: 700;
        cursor: pointer;
    }

    .sales-remove-image:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }

    /*
     * FORM ACTIONS
     */

    .sales-form-actions {
        grid-column: 1 / -1;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 22px;
        margin-top: 2px;
        padding-top: 21px;
        border-top: 1px solid #edf0ee;
    }

    .sales-publish-note {
        display: flex;
        align-items: flex-start;
        gap: 9px;
        max-width: 570px;
        color: #7a847f;
        font-size: 11px;
        line-height: 1.55;
    }

    .sales-publish-note svg {
        flex: 0 0 auto;
        margin-top: 1px;
        color: #44836d;
    }

    .sales-create-button {
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-width: 145px;
        min-height: 42px;
        padding: 9px 17px;
        border: 1px solid #236f56;
        border-radius: 11px;
        background: #28795e;
        color: #ffffff;
        box-shadow:
            0 7px 16px
            rgba(
                31,
                106,
                79,
                0.13
            );
        font-size: 12px;
        font-weight: 750;
        cursor: pointer;
        transition:
            background 0.15s ease,
            border-color 0.15s ease,
            transform 0.15s ease,
            box-shadow 0.15s ease;
    }

    .sales-create-button:hover:not(
        :disabled
    ) {
        background: #216b52;
        border-color: #1c6049;
        transform: translateY(
            -1px
        );
        box-shadow:
            0 9px 20px
            rgba(
                31,
                106,
                79,
                0.17
            );
    }

    .sales-create-button:disabled {
        opacity: 0.62;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }

    /*
     * SPINNER
     */

    .sales-button-spinner {
        display: inline-block;
        width: 14px;
        height: 14px;
        border: 2px solid
            rgba(
                119,
                72,
                64,
                0.18
            );
        border-top-color: #945046;
        border-radius: 50%;
        animation:
            salesSpin
            0.75s
            linear
            infinite;
    }

    .sales-button-spinner-light {
        border-color:
            rgba(
                255,
                255,
                255,
                0.3
            );
        border-top-color:
            #ffffff;
    }

    @keyframes salesSpin {
        to {
            transform:
                rotate(
                    360deg
                );
        }
    }

    /*
     * RESPONSIVE
     */

    @media (
        max-width: 860px
    ) {
        .sales-card-grid {
            grid-template-columns:
                1fr;
        }

        .sales-form {
            grid-template-columns:
                1fr;
        }

        .sales-field-full {
            grid-column:
                auto;
        }

        .sales-form-actions {
            grid-column:
                auto;
            flex-direction:
                column;
            align-items:
                stretch;
        }

        .sales-create-button {
            width: 100%;
        }
    }

    @media (
        max-width: 620px
    ) {
        .sales-page {
            padding-left: 0;
            padding-right: 0;
        }

        .sales-panel-heading,
        .sales-create-heading {
            padding:
                20px
                18px
                18px;
        }

        .sales-card-grid {
            padding: 15px;
        }

        .sales-form {
            padding:
                20px
                18px
                22px;
        }

        .sales-card-image-wrap {
            height: 175px;
        }

        .sales-image-preview {
            height: 245px;
        }
    }
`;